/**
 * SettingRow — one setting (design system): its name (`cardTitle`) and a line
 * of help (`meta`) on the left; where its value comes from (a state chip and
 * its action) and the control on the right. On phones the two halves stack.
 * Rows sit in a flush panel, divided by `borderSubtle` hairlines; a row with
 * unsaved edits gets a `brandSubtle` tint and a `brand` edge.
 */
import React, { useId } from 'react';
import { textRole, tokenVars } from '@turn-connect/ui';
import { useResponsive } from '@turn-connect/hooks';

export interface SettingRowProps {
  label: React.ReactNode;
  help?: React.ReactNode;
  /**
   * The id of a single control, so the label is clickable and read out. Leave
   * it out for a composite control (a chip picker, a group of buttons): the
   * control is then wrapped in a group named by the label.
   */
  htmlFor?: string;
  /** Where the value comes from: a state chip and its Customize / Use account value action. */
  source?: React.ReactNode;
  /** Long controls (chip pickers, text areas) sit under the label at every width. */
  wide?: boolean;
  /** Marks the row while it has unsaved edits. */
  dirty?: boolean;
  children: React.ReactNode;
  testId?: string;
}

const labelStyle: React.CSSProperties = { ...textRole('cardTitle'), fontSize: 13, lineHeight: '19px', display: 'block' };

export const SettingRow: React.FC<SettingRowProps> = ({ label, help, htmlFor, source, wide, dirty, children, testId }) => {
  const { isMobile } = useResponsive();
  const labelId = useId();
  const stacked = wide || isMobile;
  return (
    <div
      data-testid={testId}
      data-dirty={dirty ? 'true' : undefined}
      style={{
        display: 'grid',
        gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: stacked ? 10 : '8px 24px',
        padding: isMobile ? '12px 14px' : '12px 16px',
        minHeight: 44,
        boxSizing: 'border-box',
        borderTop: `1px solid ${tokenVars.borderSubtle}`,
        background: dirty ? tokenVars.brandSubtle : undefined,
        boxShadow: dirty ? `inset 3px 0 0 ${tokenVars.brand}` : undefined,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {htmlFor ? (
          <label id={labelId} htmlFor={htmlFor} style={labelStyle}>
            {label}
          </label>
        ) : (
          <span id={labelId} style={labelStyle}>
            {label}
          </span>
        )}
        {help && <div style={{ ...textRole('meta'), marginTop: 2 }}>{help}</div>}
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 12,
          justifyContent: stacked ? 'space-between' : 'flex-end',
          minWidth: 0,
        }}
      >
        {source}
        <div
          role={htmlFor ? undefined : 'group'}
          aria-labelledby={htmlFor ? undefined : labelId}
          style={{ marginLeft: stacked && !source ? 'auto' : undefined, minWidth: 0, maxWidth: '100%' }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default SettingRow;
